import { supabase } from '../config/supabase';
import logger from '../utils/logger';

/**
 * Reply Validation Service
 * Handles AI-based validation of email replies
 * Classifies replies as: valid (genuine), auto_reply, or out_of_office
 */
class ReplyValidationService {
  constructor() {
    // n8n webhook URL for AI reply validation (from environment)
    this.n8nWebhookUrl = process.env.REACT_APP_N8N_REPLY_VALIDATION_WEBHOOK_URL || '';
    this.validationStatuses = {
      PENDING: 'pending',
      VALID: 'valid',
      AUTO_REPLY: 'auto_reply',
      OUT_OF_OFFICE: 'out_of_office'
    };
  }

  /**
   * Send a reply event for AI validation via n8n
   * Called automatically when a new reply is received
   */
  async sendForValidation(replyEvent) {
    try {
      logger.log('📤 [WEBHOOK] Sending reply for AI validation:', replyEvent.id);
      logger.log('📤 [WEBHOOK] Target URL:', this.n8nWebhookUrl);

      // Validate required fields before sending
      if (!replyEvent.id) {
        logger.error('❌ [WEBHOOK] Missing event_id - cannot send for validation');
        return { success: false, error: 'Missing event_id' };
      }

      if (!replyEvent.lead_email) {
        logger.error('❌ [WEBHOOK] Missing lead_email - cannot send for validation');
        return { success: false, error: 'Missing lead_email' };
      }

      // Prepare validation payload with all required data for AI analysis
      const payload = {
        event_id: replyEvent.id,
        lead_email: replyEvent.lead_email,
        reply_text: replyEvent.reply_text || '',
        reply_subject: replyEvent.reply_subject || '',
        reply_html: replyEvent.reply_html || '',
        campaign_id: replyEvent.campaign_id || '',
        campaign_name: replyEvent.campaign_name || '',
        lead_id: replyEvent.lead_id || null,
        created_at: replyEvent.created_at || new Date().toISOString(),
        metadata: replyEvent.metadata || {},
        workspace_id: replyEvent.workspace_id || null
      };

      logger.log('📋 [WEBHOOK] Validation payload prepared:', {
        event_id: payload.event_id,
        lead_email: payload.lead_email,
        subject: payload.reply_subject,
        text_length: payload.reply_text.length,
        campaign: payload.campaign_name
      });

      // Send to n8n webhook for AI validation
      if (this.n8nWebhookUrl) {
        logger.log('🚀 [WEBHOOK] Calling n8n webhook...');
        
        const response = await fetch(this.n8nWebhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        logger.log(' [WEBHOOK] Response status:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          logger.error(' [WEBHOOK] Failed to send reply for validation:', response.status, response.statusText, errorText);
          
          // If webhook fails, fall back to local validation to ensure scoring isn't skipped
          logger.log(' [WEBHOOK] Falling back to local heuristics due to webhook failure');
          return await this.validateLocally(replyEvent);
        }

        // Try to parse response
        let responseData = null;
        try {
          responseData = await response.json();
          logger.log('✅ [WEBHOOK] Response data:', responseData);
        } catch (parseError) {
          logger.log('✅ [WEBHOOK] Reply sent (no JSON response)');
        }

        logger.log('✅ [WEBHOOK] Reply successfully sent for AI validation:', replyEvent.id);
        return { success: true, event_id: replyEvent.id, response: responseData };
      } else {
        // Fallback: Use local heuristics if n8n webhook not configured
        logger.log('⚠️ [WEBHOOK] n8n webhook URL not configured, using local heuristics');
        return await this.validateLocally(replyEvent);
      }
    } catch (error) {
      logger.error('❌ [WEBHOOK] Error sending reply for validation:', error);
      
      // On any error, fall back to local validation
      logger.log('⚠️ [WEBHOOK] Falling back to local heuristics due to error');
      try {
        return await this.validateLocally(replyEvent);
      } catch (localError) {
        logger.error('❌ [WEBHOOK] Local validation also failed:', localError);
        return { success: false, error: error.message };
      }
    }
  }

  /**
   * Local heuristic validation (fallback when n8n not available)
   * Basic detection of auto-replies and out-of-office messages
   */
  async validateLocally(replyEvent) {
    try {
      const replyText = (replyEvent.reply_text || '').toLowerCase();
      const replySubject = (replyEvent.reply_subject || '').toLowerCase();
      
      // Auto-reply detection patterns
      const autoReplyPatterns = [
        /auto[- ]?reply/i,
        /automatic[- ]?reply/i,
        /auto[- ]?response/i,
        /do[- ]?not[- ]?reply/i,
        /this is an automated/i,
        /this email was sent automatically/i,
        /automated message/i,
        /auto-generated/i,
        /noreply/i,
        /no-reply/i
      ];

      // Out-of-office detection patterns
      const oooPatterns = [
        /out of (the )?office/i,
        /out-of-office/i,
        /ooo/i,
        /on vacation/i,
        /on leave/i,
        /away from (my )?desk/i,
        /limited access to email/i,
        /will be back/i,
        /returning on/i,
        /away until/i,
        /currently out/i,
        /maternity leave/i,
        /paternity leave/i,
        /holiday/i,
        /sick leave/i,
        /traveling/i,
        /away message/i
      ];

      let validationStatus = this.validationStatuses.VALID;
      let confidence = 0.7; // Default confidence for local validation
      let detectedPattern = null;

      // Check for auto-reply patterns
      for (const pattern of autoReplyPatterns) {
        if (pattern.test(replyText) || pattern.test(replySubject)) {
          validationStatus = this.validationStatuses.AUTO_REPLY;
          confidence = 0.85;
          detectedPattern = pattern.toString();
          break;
        }
      }

      // Check for out-of-office patterns (only if not already detected as auto-reply)
      if (validationStatus === this.validationStatuses.VALID) {
        for (const pattern of oooPatterns) {
          if (pattern.test(replyText) || pattern.test(replySubject)) {
            validationStatus = this.validationStatuses.OUT_OF_OFFICE;
            confidence = 0.85;
            detectedPattern = pattern.toString();
            break;
          }
        }
      }

      // Update the validation status in database
      const result = await this.updateValidationStatus(
        replyEvent.id,
        validationStatus,
        {
          validation_method: 'local_heuristics',
          detected_pattern: detectedPattern,
          ai_confidence: confidence
        }
      );

      return result;
    } catch (error) {
      logger.error('Error in local validation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update validation status for a reply event
   * Called by n8n workflow or local validation
   */
  async updateValidationStatus(eventId, validationStatus, metadata = {}) {
    try {
      logger.log('📝 Updating validation status:', eventId, validationStatus);

      // Call the database function to update status and handle scoring
      const { data, error } = await supabase.rpc('update_reply_validation_status', {
        p_event_id: eventId,
        p_validation_status: validationStatus,
        p_validation_metadata: metadata,
        p_ai_confidence: metadata.ai_confidence || null
      });

      if (error) {
        logger.error('Error updating validation status:', error);
        return { success: false, error: error.message };
      }

      logger.log('✅ Validation status updated:', data);
      return data;
    } catch (error) {
      logger.error('Error updating validation status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get reply events with validation filtering
   * Supports campaign-based filtering
   */
  async getValidatedReplies(options = {}) {
    try {
      const {
        validationFilter = null, // 'valid', 'auto_reply', 'out_of_office', or null for all
        campaignId = null,
        limit = 100,
        offset = 0
      } = options;

      const { data, error } = await supabase.rpc('get_validated_reply_events', {
        p_validation_filter: validationFilter,
        p_campaign_id: campaignId,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        logger.error('Error getting validated replies:', error);
        return { success: false, error: error.message, data: [] };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      logger.error('Error getting validated replies:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get count of replies by validation status
   */
  async getValidationCounts(campaignId = null) {
    try {
      let query = supabase
        .from('lead_events')
        .select('validation_status', { count: 'exact', head: false })
        .eq('event_type', 'reply')
        .not('reply_text', 'is', null);

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error getting validation counts:', error);
        return { pending: 0, valid: 0, auto_reply: 0, out_of_office: 0 };
      }

      // Count by status
      const counts = {
        pending: 0,
        valid: 0,
        auto_reply: 0,
        out_of_office: 0
      };

      data.forEach(item => {
        const status = item.validation_status || 'pending';
        if (counts.hasOwnProperty(status)) {
          counts[status]++;
        }
      });

      return counts;
    } catch (error) {
      logger.error('Error getting validation counts:', error);
      return { pending: 0, valid: 0, auto_reply: 0, out_of_office: 0 };
    }
  }

  /**
   * Check if a reply should trigger notifications
   * Only valid replies trigger notifications
   */
  shouldNotify(validationStatus) {
    return validationStatus === this.validationStatuses.VALID;
  }

  /**
   * Check if a reply should contribute to scoring
   * Only valid replies contribute to scoring
   */
  shouldApplyScore(validationStatus) {
    return validationStatus === this.validationStatuses.VALID;
  }

  /**
   * Get validation status display info (for UI)
   */
  getValidationDisplayInfo(validationStatus) {
    const displayInfo = {
      [this.validationStatuses.PENDING]: {
        label: 'Pending',
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        icon: 'clock'
      },
      [this.validationStatuses.VALID]: {
        label: 'Valid Reply',
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-600',
        icon: 'check-circle'
      },
      [this.validationStatuses.AUTO_REPLY]: {
        label: 'Auto Reply',
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-600',
        icon: 'robot'
      },
      [this.validationStatuses.OUT_OF_OFFICE]: {
        label: 'Out of Office',
        color: 'yellow',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-600',
        icon: 'calendar-off'
      }
    };

    return displayInfo[validationStatus] || displayInfo[this.validationStatuses.PENDING];
  }
}

// Export singleton instance
export const replyValidationService = new ReplyValidationService();
export default replyValidationService;
